import React, { useState } from "react";
import { useSelector } from "react-redux";
import { RootStatePlatform } from "../../store/platform-slice";
import { Theme } from "../../../../providers/ThemeProvider";
import LabSvg from "../../../../common/svgs/LabSvg";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { parse, parseISO } from "date-fns";
import { formatDisplayDate } from "../../../../common/utils/date";
import useTheme from "../../../../common/hooks/useTheme";

// `dimmed` fades the trigger chip only (sample-report treatment on the
// Health overview) — the opened popover stays fully opaque and readable,
// and keeps painting above the content below it.
const TestIncludedCard = ({ dimmed = false }: { dimmed?: boolean }) => {
  const theme: Theme = useTheme();
  const [isTestIncludedOpen, setIsTestIncludedOpen] = useState<boolean>(false);

  const reportData = useSelector(
    (state: RootStatePlatform) => state.platform.dataPlatformServer.score
  );

  return (
    <div style={styles.wrapper}>
      <div
        style={{
          ...styles.testIncludedContainer(theme),
          ...(dimmed ? { opacity: 0.5 } : {}),
        }}
      >
        <div style={styles.testIncludedHeader}>
          <LabSvg />
          <div style={styles.testIncludedTitle(theme)}>Test included</div>
          <div
            style={styles.iconWrapper(isTestIncludedOpen)}
            onClick={() => setIsTestIncludedOpen(!isTestIncludedOpen)}
          >
            <KeyboardArrowRightIcon />
          </div>
        </div>
      </div>
      {isTestIncludedOpen && (
        <div style={styles.testIncludedDetailWrapper(theme)}>
          {!reportData.testIncludedTable?.length && (
            <div style={styles.emptyMessage(theme)}>No available tests</div>
          )}
          {reportData.testIncludedTable?.map((test, index: number) => {
            const testDate = parse(test.date, "MM-dd-yyyy", new Date());

            return (
              <div
                key={index}
                style={{
                  ...styles.testIncludedDetail,
                  borderBottom:
                    index !== reportData.testIncludedTable.length - 1
                      ? `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`
                      : "",
                }}
              >
                <div style={styles.testIncludedDetailLabel(theme)}>
                  {test.label}
                </div>
                <div style={styles.testIncludedDetailDate(theme)}>
                  {formatDisplayDate(testDate)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TestIncludedCard;

const styles = {
  wrapper: {
    position: "relative" as "relative",
    zIndex: 20,
  },
  testIncludedContainer: (theme: Theme) => ({
    padding: "8px 12px",
    borderRadius: "8px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
  }),
  testIncludedHeader: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },
  testIncludedTitle: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
  }),
  iconWrapper: (open: boolean) => ({
    width: "12px",
    height: "12px",
    transform: open ? "rotate(270deg)" : "rotate(90deg)",
    transition: "transform 0.3s",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
  }),
  testIncludedDetailWrapper: (theme: Theme) => ({
    position: "absolute" as "absolute",
    top: "100%",
    right: 0,
    marginTop: "4px",
    padding: "12px",
    borderRadius: "8px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    boxShadow: "var(--box-shadow-general)",
    zIndex: 10,
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
    minWidth: "100%",
  }),
  testIncludedDetail: {
    paddingBottom: "8px",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
  },
  testIncludedDetailLabel: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Bold)",
    fontSize: "12px",
  }),
  emptyMessage: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
  }),
  testIncludedDetailDate: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Medium)",
    fontSize: "10px",
  }),
};
